import { Fragment, useMemo, useState } from 'react'
import {
  BALANCE_CURVES,
  BALANCE_FIELDS,
  BALANCE_GROUPS,
  BALANCE_MASS_RANGE,
  DEFAULT_BALANCE,
  balanceCurve,
  balanceValues,
  isMassFree,
  type BalanceParams,
  type BalanceRow,
  type BalanceValues,
} from '@/domain/hs1'
import { Alert, Button, Card, Table, Td, Th } from '@/components/ui'
import { StagePanel } from '@/components/stage'
import { CycleChart } from '@/sim/CycleChart'
import { PageHead, Slider } from '@/sim/parts'

/**
 * Massenbilanz — was am Balancieren an der Masse hängt.
 *
 * DIE SEITE IST EIN ARGUMENT, KEINE ANZEIGE. Sie beantwortet die Frage „was
 * wird besser, wenn der Roboter leichter wird?" und ihre Antwort ist
 * unbequem: die halbe Masse macht das Balancieren nicht doppelt so leicht.
 * Zeitkonstante, Capture Point und der auffangbare IMPULS ändern sich gar
 * nicht. Besser wird nur, was über das Moment läuft.
 *
 * Deshalb steht die Spalte „skaliert mit" gleichberechtigt neben den Zahlen,
 * und deshalb sind die massenfreien Kurven im Diagrammteil eigens gekennzeichnet
 * — sie sind der Befund, nicht der uninteressante Rest.
 */

const fmt = (value: number, digits: number): string => {
  if (!Number.isFinite(value)) return '∞'
  return value.toFixed(digits).replace('.', ',')
}

/** Das Verhältnis B zu A, mit der Färbung, die es verdient. */
function ratioCell(row: BalanceRow, a: BalanceValues, b: BalanceValues) {
  const va = row.value(a)
  const vb = row.value(b)
  if (!Number.isFinite(va) || !Number.isFinite(vb) || va === 0) {
    return { text: '—', tone: 'neutral' as const }
  }
  const q = vb / va
  const tone =
    Math.abs(q - 1) < 0.02 ? ('neutral' as const) : q > 1 ? ('up' as const) : ('down' as const)
  return { text: `×${fmt(q, 2)}`, tone }
}

export function BalancePage() {
  const [params, setParams] = useState<BalanceParams>(DEFAULT_BALANCE)

  const set = (key: keyof BalanceParams) => (value: number) =>
    setParams((prev) => ({ ...prev, [key]: value }))

  /*
    Die beiden Massen dürfen nicht gleich sein — sonst ist jedes Verhältnis eins
    und die Seite sagt nichts. Statt die Eingabe zu verbieten, wird die zweite
    Masse hier um ein Kilogramm angehoben: der Regler bleibt frei bedienbar, und
    die Tabelle bleibt lesbar.
  */
  const massB = params.massB === params.massA ? params.massA + 1 : params.massB

  const a = useMemo(() => balanceValues(params, params.massA), [params])
  const b = useMemo(() => balanceValues(params, massB), [params, massB])

  const curves = useMemo(
    () =>
      BALANCE_CURVES.map((curve) => {
        const values = balanceCurve(params, curve)
        return { curve, values, massFree: isMassFree(values) }
      }),
    [params],
  )

  const { min, max } = BALANCE_MASS_RANGE

  return (
    <div className="stack">
      <PageHead
        title="Balance"
        actions={
          <Button size="sm" variant="ghost" onClick={() => setParams(DEFAULT_BALANCE)}>
            Zurücksetzen
          </Button>
        }
      />

      {/* Das Bühnenfenster steht auf jeder Seite, auf der man die Bewegung
          verstellt. Beim Scrollen heftet es sich oben an: der Kopftext wird
          einmal gelesen, die Figur wird immer gebraucht. */}
      <StagePanel />

      <Alert tone="info" title="Der Befund">
        Die Grunddynamik des Balancierens enthält keine Masse. Massenabhängig ist alles, was über
        das <b>Moment</b> läuft — und die Rückkopplung, dass jedes zusätzliche Newtonmeter
        Aktuatormasse kostet, die wieder Moment verlangt.
      </Alert>

      <Card title="Eingaben">
        <div className="workbench">
          {BALANCE_FIELDS.map((field) => (
            <Slider
              key={field.key}
              label={field.label}
              value={field.key === 'massB' ? massB : params[field.key]}
              min={field.min}
              max={field.max}
              step={field.step}
              unit={field.unit}
              decimals={field.digits}
              onChange={set(field.key)}
            />
          ))}
        </div>
      </Card>

      <Card
        title="Ergebnisse im Vergleich"
        eyebrow={`Massenverhältnis ×${fmt(massB / params.massA, 2)}${
          params.payload ? ` · Nutzlast ${fmt(params.payload, 0)} kg` : ''
        } · Schwerpunkthöhe ${fmt(params.comHeight, 2)} m · Gelenkmoment ${fmt(
          params.peakMoment,
          0,
        )} Nm · Regelverzug ${fmt(params.latency, 3)} s`}
      >
        <Table>
          <thead>
            <tr>
              <Th>Grösse</Th>
              <Th>Formel</Th>
              <Th align="right">{fmt(params.massA, 0)} kg</Th>
              <Th align="right">{fmt(massB, 0)} kg</Th>
              <Th align="right">Verhältnis</Th>
              <Th>Skaliert mit</Th>
            </tr>
          </thead>
          <tbody>
            {BALANCE_GROUPS.map((group) => (
              <Fragment key={group.title}>
                <tr>
                  <Td colSpan={6}>
                    <span className="eyebrow">{group.title}</span>
                  </Td>
                </tr>
                {group.rows.map((row) => {
                  const ratio = ratioCell(row, a, b)
                  return (
                    <tr key={row.label}>
                      <Td>{row.label}</Td>
                      <Td>
                        <code className="balance__formula">{row.formula}</code>
                      </Td>
                      <Td align="right" numeric>
                        {row.prefix ?? ''}
                        {fmt(row.value(a), row.digits)}
                        {row.unit ? ` ${row.unit}` : ''}
                      </Td>
                      <Td align="right" numeric>
                        {row.prefix ?? ''}
                        {fmt(row.value(b), row.digits)}
                        {row.unit ? ` ${row.unit}` : ''}
                      </Td>
                      <Td align="right" numeric>
                        <span className={`balance__ratio balance__ratio--${ratio.tone}`}>
                          {ratio.text}
                        </span>
                      </Td>
                      <Td>
                        <span className="balance__scaling">{row.scaling}</span>
                      </Td>
                    </tr>
                  )
                })}
              </Fragment>
            ))}
          </tbody>
        </Table>
      </Card>

      <Card
        title="Verlauf über der Masse"
        eyebrow={`${min} bis ${max} kg · was sich über den ganzen Bereich nicht ändert, ist gekennzeichnet`}
      >
        <div className="balance__curves">
          {curves.map(({ curve, values, massFree }) => (
            <div
              key={curve.key}
              className={`balance__curve${massFree ? ' balance__curve--free' : ''}`}
            >
              <CycleChart
                title={curve.label}
                unit={`${fmt(curve.value(a), curve.digits)} → ${fmt(
                  curve.value(b),
                  curve.digits,
                )} ${curve.unit}`}
                height={130}
                zero={false}
                /* Der Achsenfuss bliebe hier ohne Aussage: der Wertebereich
                   steht bereits in der Einheitszeile über dem Bild, und die
                   Massenachse ist in der Kopfzeile der Karte benannt. */
                foot={false}
                xDomain={[min, max]}
                xUnit="kg"
                xName="der Masse"
                xLabel="Masse"
                series={[
                  {
                    key: curve.key,
                    label: curve.label,
                    values,
                    tone: massFree ? 6 : 1,
                  },
                ]}
              />
              {massFree && (
                <p className="balance__free">
                  massenfrei — über den ganzen Bereich unverändert
                </p>
              )}
            </div>
          ))}
        </div>
      </Card>

      <Card title="Lesehinweise">
        <div className="balance__notes">
          <div>
            <span className="eyebrow">Massenfrei</span>
            <p className="page-lead">
              Zeitkonstante, Capture Point, ZMP-Bedingung und Störungswachstum über die Latenz sind
              bei 40 kg dieselben wie bei 80. Auch der <b>auffangbare Impuls</b>: ein Stoss mit
              festem Impuls erzeugt beim schwereren Körper nur die halbe Geschwindigkeit — und genau
              die halbe kann er auch noch abfangen. Beides kürzt sich heraus.
            </p>
          </div>
          <div>
            <span className="eyebrow">Massenabhängig</span>
            <p className="page-lead">
              Alles, was über das Moment läuft. Das Rückstellmoment ist m·g·d und wächst linear; bei
              festem Höchstmoment halbieren sich der haltbare Neigungswinkel und die auffangbare
              Schwerpunktgeschwindigkeit. Dazu die Folgen: Sturzenergie, kinetische Energie und
              Aufsetzimpuls wachsen linear.
            </p>
          </div>
          <div>
            <span className="eyebrow">Die Rückkopplung</span>
            <p className="page-lead">
              Liessen sich die Antriebe exakt mit der Masse mitskalieren, wäre Balancieren
              masseninvariant. Das geht aber nicht umsonst: jedes zusätzliche Newtonmeter kostet
              Aktuatormasse, und die verlangt wieder Moment. Der Faktor <b>1/(1 − f)</b> sagt, wie
              stark sich diese Schleife aufschaukelt. Nähert sich f der Eins, divergiert der Entwurf
              — dann ist mit der gewählten Momentdichte kein Roboter mehr baubar.
            </p>
          </div>
          <div>
            <span className="eyebrow">Wo die Kurzschrift trügt</span>
            <p className="page-lead">
              Drei Zeilen tragen hier eine ausgeschriebene Skalierung, wo die gängige Kurzschrift „∝
              m²" beziehungsweise „1/√m" lautet. Die Grundlast der Elektronik ist ein konstanter
              Summand und bei 40 kg der grössere Teil der Halteleistung; die zulässige
              Kontaktgeschwindigkeit sättigt, weil µ<sub>red</sub> eine reduzierte Masse ist und
              gegen die effektive Körpermasse des Menschen läuft. Wer nach der Kurzschrift die
              vierfache Standzeit erwartet, bekommt den Faktor zwei.
            </p>
          </div>
          <p className="page-lead">
            <span className="eyebrow">Herkunft</span> Die Grenzwerte für die Kontaktgeschwindigkeit
            sind aus den in ISO/TS 15066 veröffentlichten Beispielwerten für den Brustkorb
            zurückgerechnet (effektive Körpermasse 40 kg, Energiegrenze rund 1,08 J); die bewegte
            Ersatzmasse ist nach der Norm Nutzlast plus halbe Gesamtmasse. Für eine Abnahme braucht
            es die Norm selbst, nicht diese Seite.
          </p>
        </div>
      </Card>
    </div>
  )
}
